import { ONLINE_APPLICATION_SERVICE_BASE_URL } from "env";
import { createCommonHeaders } from "./commonHeaders";
import {
    get_GetAllFeatureFlags,
    get_GetFeatureFlagByKey,
    get_GetApplication,
    get_GetQuote,
    post_CheckIdentity,
    post_FindOffer,
    post_GenerateFastPolicyNumber,
    post_Submit,
    post_SubmitGIWLApplication,
    post_SubmitMLTAApplication,
    put_StoreApplication,
    put_StoreQuote,
    put_StoreSessionId,
} from "./online-application-service.openapi.zod";
import { z } from "zod";

type FindOfferRequestBody =
    | z.infer<typeof post_FindOffer.parameters.shape.body>
    // FUTURE: Remove this custom type after OpenAPI spec for service change is finalized
    | { purl: string };
export type FindOfferResponseBody = z.infer<typeof post_FindOffer.response>;

/** Find offer details by finderNumber */
export async function findOffer(requestBody: FindOfferRequestBody): Promise<FindOfferResponseBody> {
    const response = await fetch(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/findOffer`, {
        method: "POST",
        body: JSON.stringify(requestBody),
        headers: {
            ...createCommonHeaders(window),
            "Content-Type": "application/json",
        },
    });
    if (!response.ok) {
        if (response.status === 404) {
            console.warn(response);
            throw new Error("Unable to find offer.");
        } else {
            console.warn(response);
            throw new Error("Unexpected response to request to findOffer.");
        }
    }
    return post_FindOffer.response.parse(await response.json());
}

export type CheckIdentityRequestBody = z.infer<typeof post_CheckIdentity.parameters.shape.body>;
type CheckIdentityResponseBody = z.infer<typeof post_CheckIdentity.response>;

/** Check identity of person via the /checkIdentity endpoint */
export async function checkIdentity(person: CheckIdentityRequestBody): Promise<CheckIdentityResponseBody> {
    const response = await fetch(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/checkIdentity`, {
        method: "POST",
        body: JSON.stringify(person),
        headers: {
            ...createCommonHeaders(window),
            "Content-Type": "application/json",
        },
    });
    if (!response.ok) {
        if (response.status === 404) {
            console.warn(response);
            throw new Error("Unable to check identity.");
        } else {
            console.warn(response);
            throw new Error("Unexpected response to request to checkIdentity.");
        }
    }

    return post_CheckIdentity.response.parse(await response.json());
}

type GenerateFastPolicyNumberResponseBody = z.infer<typeof post_GenerateFastPolicyNumber.response>;

/** Generate a new FAST policy number via the /generateFastPolicyNumber endpoint (takes no request body) */
export async function generateFastPolicyNumber(): Promise<GenerateFastPolicyNumberResponseBody> {
    const response = await fetch(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/generateFastPolicyNumber`, {
        method: "POST",
        headers: {
            ...createCommonHeaders(window),
            "Content-Type": "application/json",
        },
    });
    if (!response.ok) {
        console.warn(response);
        throw new Error("Unable to generate policy number.");
    }

    return post_GenerateFastPolicyNumber.response.parse(await response.json());
}

type DirectTermSubmissionRequestBody = z.infer<typeof post_Submit.parameters.shape.body>;
type DirectTermSubmissionResponseBody = z.infer<typeof post_Submit.response>;

/** Send the application via the /submit endpoint */
export async function submit(application: DirectTermSubmissionRequestBody): Promise<DirectTermSubmissionResponseBody> {
    const response = await fetch(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/submit`, {
        method: "POST",
        body: JSON.stringify(application),
        headers: {
            ...createCommonHeaders(window),
            "Content-Type": "application/json",
        },
    });
    if (!response.ok) {
        console.warn(response);
        throw new Error("Unable to submit application.");
    }

    return post_Submit.response.parse(await response.json());
}

type LoyaltySubmissionRequestBody = z.infer<typeof post_SubmitMLTAApplication.parameters.shape.body>;
type LoyaltySubmissionResponseBody = z.infer<typeof post_SubmitMLTAApplication.response>;

/** Send the application via the /submitMLTAApplication endpoint  */
export async function submitMLTAApplication(application: LoyaltySubmissionRequestBody): Promise<LoyaltySubmissionResponseBody> {
    const response = await fetch(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/submitMLTAApplication`, {
        method: "POST",
        body: JSON.stringify(application),
        headers: {
            ...createCommonHeaders(window),
            "Content-Type": "application/json",
        },
    });
    if (!response.ok) {
        console.warn(response);
        throw new Error("Unable to submit application.");
    }

    return post_SubmitMLTAApplication.response.parse(await response.json());
}

type GiwlSubmissionRequestBody = z.infer<typeof post_SubmitGIWLApplication.parameters.shape.body>;
type GiwlSubmissionResponseBody = z.infer<typeof post_SubmitGIWLApplication.response>;

/** Send the application via the /submitGIWLApplication endpoint  */
export async function submitGIWLApplication(application: GiwlSubmissionRequestBody): Promise<GiwlSubmissionResponseBody> {
    const response = await fetch(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/submitGIWLApplication`, {
        method: "POST",
        body: JSON.stringify(application),
        headers: {
            ...createCommonHeaders(window),
            "Content-Type": "application/json",
        },
    });
    if (!response.ok) {
        console.warn(response);
        throw new Error("Unable to submit application.");
    }

    return post_SubmitGIWLApplication.response.parse(await response.json());
}

export type StoreQuoteRequestBody = z.infer<typeof put_StoreQuote.parameters.shape.body>;
export async function storeQuote(body: StoreQuoteRequestBody): Promise<boolean> {
    const response = await fetch(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/storeQuote`, {
        method: "PUT",
        body: JSON.stringify(body),
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });
    return response.ok;
}

export type StoreAppRequestBody = z.infer<typeof put_StoreApplication.parameters.shape.body>;
export async function storeApp(body: StoreAppRequestBody): Promise<boolean> {
    const response = await fetch(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/storeApplication`, {
        method: "PUT",
        body: JSON.stringify(body),
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });
    return response.ok;
}

export type StoreSessionIdRequestBody = z.infer<typeof put_StoreSessionId.parameters.shape.body>;
export async function storeSessionId(body: StoreSessionIdRequestBody): Promise<boolean> {
    const response = await fetch(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/storeSessionId`, {
        method: "PUT",
        body: JSON.stringify(body),
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });
    return response.ok;
}

export type GetQuoteResponseBody = z.infer<typeof get_GetQuote.response>;
export async function getQuote(userRef: string): Promise<GetQuoteResponseBody> {
    const url = new URL(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/getQuote`);
    url.search = new URLSearchParams({ userRef }).toString();
    const response = await fetch(url, {
        method: "GET",
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        console.warn(response);
        const error = new Error("Unable to get quote.") as Error & { status: number };
        error.status = response.status;
        throw error;
    }

    return get_GetQuote.response.parse(await response.json());
}

export type GetApplicationResponseBody = z.infer<typeof get_GetApplication.response>;
export async function getApplication(userRef: string): Promise<GetApplicationResponseBody> {
    const url = new URL(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/getApplication`);
    url.search = new URLSearchParams({ userRef }).toString();
    const response = await fetch(url, {
        method: "GET",
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        console.warn(response);
        const error = new Error("Unable to get application.") as Error & { status: number };
        error.status = response.status;
        throw error;
    }

    return get_GetApplication.response.parse(await response.json());
}

export type AuthenticateResumeRequestBody = {
    lastName: string;
    dateOfBirth: string;
    saveContId: string;
};

export type AuthenticateResumeResponseBody = {
    isAuthenticated: boolean;
    message?: string;
};

/** Authenticate resume via the /authenticateResume endpoint */
export async function authenticateResume(body: AuthenticateResumeRequestBody): Promise<AuthenticateResumeResponseBody> {
    const response = await fetch(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/authenticateResume`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const error = new Error("Your information does not match. Please try again or start a new application.") as Error & {
            status: number;
        };
        error.status = response.status;
        throw error;
    }

    return await response.json();
}

/** Get the feature flag by flagKey via the /getFeatureFlagByKey endpoint */
export type GetFeatureFlagByKeyResponseBody = z.infer<typeof get_GetFeatureFlagByKey.response>;
export async function getFeatureFlagByKey(flagKey: string): Promise<GetFeatureFlagByKeyResponseBody> {
    const url = new URL(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/getFeatureFlagByKey/${flagKey}`);
    const response = await fetch(url, {
        method: "GET",
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        console.warn(response);
        throw new Error("Unable to get feature flag by given flagKey: " + flagKey);
    }

    return get_GetFeatureFlagByKey.response.parse(await response.json());
}

export type GetFinderNumberBySaveContIdResponse = {
    finderNumber: string;
};

export async function getFinderNumberBySaveContId(saveContId: string): Promise<GetFinderNumberBySaveContIdResponse | null> {
    const url = new URL(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/getFinderNumber`);
    url.search = new URLSearchParams({ saveContId: saveContId }).toString();
    const response = await fetch(url, {
        method: "GET",
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        const error = new Error("There was an error retrieving application.") as Error & {
            status: number;
        };
        error.status = response.status;
        throw error;
    }

    return response.json();
}

/** Get all feature flags via the /getAllFeatureFlags endpoint */
export type GetAllFeatureFlagsResponseBody = z.infer<typeof get_GetAllFeatureFlags.response>;
export async function getAllFeatureFlags(): Promise<GetAllFeatureFlagsResponseBody> {
    const url = new URL(`${ONLINE_APPLICATION_SERVICE_BASE_URL}/getFeatureFlags`);
    const response = await fetch(url, {
        method: "GET",
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if (!response.ok) {
        console.warn(response);
        throw new Error("Unable to get all feature flags");
    }

    return get_GetAllFeatureFlags.response.parse(await response.json());
}
